import React, { useEffect, useState } from 'react';
import api from './api';
import Kanban from './Kanban';
import Gantt from './Gantt';
import ActivityLog from './ActivityLog';
import ProgressChart from './ProgressChart';
import CalendarView from './CalendarView';
import TaskListView from './TaskListView';
import TaskModal from './TaskModal';
import ProjectModal from './ProjectModal';
import CancelProjectModal from './CancelProjectModal';
import Deliverables from './Deliverables';
import ProjectReport from './ProjectReport';
import ProjectBudget from './ProjectBudget';
import ImportTasksModal from './ImportTasksModal';
import MergeTasksModal from './MergeTasksModal';
import ProjectCharter from './ProjectCharter';
import MilestonesPanel from './MilestonesPanel';
import { projectCode, PROJECT_STATUS_LABELS, PROJECT_STATUS_BADGE_CLASS, PROJECT_TYPE_BADGE_CLASS, libelleType, libelleService, CAN_CANCEL_PROJECT_ROLES, CAN_ARCHIVE_PROJECT_ROLES, CAN_IMPORT_TASKS_ROLES, formatDate, formatMontant, userDisplayName } from './helpers';

// Six onglets au lieu de dix : tout ce qui touche au planning (Kanban, liste,
// Gantt, calendrier, jalons) vit dans « Planification », avec un sélecteur de
// vue à l'intérieur. La dernière vue choisie est mémorisée sur ce poste.
const TABS = [
  { key: 'charte', label: 'Charte' },
  { key: 'planif', label: 'Planification' },
  { key: 'budget', label: 'Budget' },
  { key: 'deliverables', label: 'Livrables' },
  { key: 'report', label: 'Rapport' },
  { key: 'activity', label: 'Activité' },
];

const VUES_PLANIF = [
  { key: 'kanban', label: 'Kanban' },
  { key: 'list', label: 'Liste des tâches' },
  { key: 'gantt', label: 'Gantt' },
  { key: 'calendar', label: 'Calendrier' },
  { key: 'jalons', label: 'Jalons' },
];
const VUES_PLANIF_KEYS = VUES_PLANIF.map((v) => v.key);
const CLE_VUE_PLANIF = 'vigie-vue-planif';

function vuePlanifEnregistree() {
  try {
    const v = localStorage.getItem(CLE_VUE_PLANIF);
    return VUES_PLANIF_KEYS.includes(v) ? v : 'kanban';
  } catch (e) {
    return 'kanban';
  }
}

// Un ancien onglet (kanban, gantt…) demandé depuis ailleurs se traduit en
// « Planification » + la vue correspondante.
function resoudreOnglet(onglet) {
  if (!onglet) return { tab: 'planif', vue: null };
  if (VUES_PLANIF_KEYS.includes(onglet)) return { tab: 'planif', vue: onglet };
  return { tab: onglet, vue: null };
}

function ProjectDetail({ project, users, currentUser, onBack, onProjectChanged, openTaskId, onTaskOpened, ongletInitial }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const initial = resoudreOnglet(ongletInitial);
  const [activeTab, setActiveTab] = useState(initial.tab);
  const [vuePlanif, setVuePlanifState] = useState(initial.vue || vuePlanifEnregistree());
  const setVuePlanif = (vue) => {
    setVuePlanifState(vue);
    try { localStorage.setItem(CLE_VUE_PLANIF, vue); } catch (e) { /* stockage indisponible */ }
  };
  const [openTask, setOpenTask] = useState(undefined); // undefined = closed, null = new task
  const [newTaskStatus, setNewTaskStatus] = useState('Todo');
  const [showEditProject, setShowEditProject] = useState(false);
  const [showCancelProject, setShowCancelProject] = useState(false);
  const [proj, setProj] = useState(project);
  const [commentCounts, setCommentCounts] = useState({});
  const [attachmentCounts, setAttachmentCounts] = useState({});
  const [deliverableCount, setDeliverableCount] = useState(0);
  const [archivage, setArchivage] = useState('');
  const [historique, setHistorique] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [showFusion, setShowFusion] = useState(false);
  const [bilanImport, setBilanImport] = useState('');
  // Menu « Actions » : les opérations rares (import, regroupement, archivage,
  // annulation) n'ont pas à occuper la ligne de titre en permanence.
  const [menuActions, setMenuActions] = useState(false);

  useEffect(() => {
    if (!menuActions) return undefined;
    const fermer = (e) => {
      if (!e.target.closest || !e.target.closest('.menu-actions')) setMenuActions(false);
    };
    const echap = (e) => { if (e.key === 'Escape') setMenuActions(false); };
    document.addEventListener('mousedown', fermer);
    document.addEventListener('keydown', echap);
    return () => {
      document.removeEventListener('mousedown', fermer);
      document.removeEventListener('keydown', echap);
    };
  }, [menuActions]);

  // Archiver ne supprime rien : le projet sort simplement des vues courantes
  // (tableau de bord, calendrier, achats, alertes) et reste consultable dans
  // l'onglet « Archivés ». On peut le restaurer à tout moment.
  const basculerArchive = (archiver) => {
    setArchivage('');
    api
      .put(`/projects/${proj.id}/archive`, { archived: archiver })
      .then((res) => {
        setProj(res.data);
        if (onProjectChanged) onProjectChanged(res.data);
      })
      .catch((err) =>
        setArchivage(err.response?.data?.message || "L'opération a échoué.")
      );
  };

  // `silencieux` : recharge les tâches sans vider l'écran. Utilisé après une
  // action faite depuis une vue (déplacer un jalon sur le Gantt), pour que la
  // vue ne soit pas démontée et remontée — sinon elle perdrait son état et
  // clignoterait à chaque geste.
  const loadTasks = (silencieux = false) => {
    if (!silencieux) setLoading(true);
    api
      .get('/tasks', { params: { projectId: proj.id } })
      .then((res) => setTasks(res.data))
      .catch(() => setError('Impossible de charger les tâches du projet.'))
      .finally(() => { if (!silencieux) setLoading(false); });
  };

  const loadCounts = () => {
    Promise.all([
      api.get('/comments', { params: { projectId: proj.id } }),
      api.get('/attachments', { params: { projectId: proj.id } }),
    ])
      .then(([commentsRes, attachmentsRes]) => {
        const cCounts = {};
        commentsRes.data.forEach((c) => {
          if (c.task_id) cCounts[c.task_id] = (cCounts[c.task_id] || 0) + 1;
        });
        const aCounts = {};
        attachmentsRes.data.forEach((a) => {
          if (a.task_id) aCounts[a.task_id] = (aCounts[a.task_id] || 0) + 1;
        });
        setCommentCounts(cCounts);
        setAttachmentCounts(aCounts);
        setDeliverableCount(attachmentsRes.data.length);
      })
      .catch(() => {});
  };

  // Courbe d'avancement du projet : un relevé par jour, côté serveur.
  useEffect(() => {
    api
      .get('/progress-history', { params: { projectId: proj.id } })
      .then((res) => setHistorique(res.data))
      .catch(() => setHistorique([]));
  }, [proj.id, tasks.length]);

  // Arriver d'un autre écran peut imposer l'onglet à afficher (création de
  // projet, clic sur une notification).
  useEffect(() => {
    if (!ongletInitial) return;
    const r = resoudreOnglet(ongletInitial);
    setActiveTab(r.tab);
    if (r.vue) setVuePlanifState(r.vue);
  }, [ongletInitial, proj.id]);

  useEffect(loadTasks, [proj.id]);
  useEffect(loadCounts, [proj.id, activeTab]);

  // Arrivée depuis une notification : dès que les tâches sont chargées, on
  // ouvre celle qui était visée. Si elle a été supprimée entre-temps, on reste
  // simplement sur le projet plutôt que d'afficher une erreur.
  useEffect(() => {
    if (!openTaskId || loading) return;
    const cible = tasks.find((t) => t.id === parseInt(openTaskId));
    if (cible) {
      setActiveTab('planif');
      setVuePlanifState('list');
      setOpenTask(cible);
    }
    if (onTaskOpened) onTaskOpened();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTaskId, loading, tasks]);

  // Après une saisie de dépense, on recharge le projet pour que les totaux
  // budgétaires affichés en tête de page suivent immédiatement.
  const rafraichirProjet = () => {
    api
      .get(`/projects/${proj.id}`)
      .then((res) => {
        setProj(res.data);
        if (onProjectChanged) onProjectChanged(res.data);
      })
      .catch(() => {});
  };

  // Les jalons sont des repères de date, pas du travail : ils ont leur propre
  // onglet et ne viennent pas encombrer le kanban, la liste ni les compteurs.
  const travail = tasks.filter((t) => !t.est_jalon);

  const openCount = travail.filter((t) => t.status !== 'Done').length;

  // Le nouvel ordre des jalons est enregistré tout de suite : on ne veut pas
  // d'un « Enregistrer » de plus pour un déplacement de deux lignes.
  const reordonnerJalons = (ids) => {
    api
      .post(`/projects/${proj.id}/jalons/ordre`, { ids })
      .then(() => loadTasks())
      .catch(() => setError('Impossible d’enregistrer l’ordre des jalons.'));
  };

  const handleOpenTask = (task, statusForNew = 'Todo') => {
    setNewTaskStatus(statusForNew);
    setOpenTask(task || null);
  };

  const handleTaskSaved = () => {
    setOpenTask(undefined);
    loadTasks();
    loadCounts();
  };

  const handleTaskDeleted = () => {
    setOpenTask(undefined);
    loadTasks();
    loadCounts();
  };

  const chef = users.find((u) => u.id === proj.project_manager_id);

  const actionsSecondaires = [];
  if (CAN_IMPORT_TASKS_ROLES.includes(currentUser?.role) && !proj.archived) {
    actionsSecondaires.push({ label: 'Importer des tâches…', action: () => setShowImport(true) });
    actionsSecondaires.push({ label: 'Regrouper des tâches…', action: () => setShowFusion(true) });
  }
  if (CAN_ARCHIVE_PROJECT_ROLES.includes(currentUser?.role)) {
    actionsSecondaires.push({
      label: proj.archived ? "Sortir de l'archive" : 'Archiver le projet',
      action: () => basculerArchive(!proj.archived),
      separateur: true,
    });
  }
  if (CAN_CANCEL_PROJECT_ROLES.includes(currentUser?.role) && proj.status !== 'Cancelled' && !proj.archived) {
    actionsSecondaires.push({ label: 'Annuler le projet…', action: () => setShowCancelProject(true), danger: true });
  }

  return (
    <div className="fade-in">
      <button className="btn" onClick={onBack} style={{ marginBottom: 16 }}>
        ← Tous les projets
      </button>

      <div className="page-header-row" style={{ alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        {/* Le nom du projet est déjà le titre de la page (en-tête) : on ne le
            répète pas. Ici, seulement ce qui le qualifie. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
          <span className="project-ref">{projectCode(proj.id)}</span>
          <span className={`badge ${PROJECT_STATUS_BADGE_CLASS[proj.status] || 'badge-active'}`}>
            {PROJECT_STATUS_LABELS[proj.status] || proj.status}
          </span>
          {proj.project_type && (
            <span className={`badge ${PROJECT_TYPE_BADGE_CLASS[proj.project_type] || 'badge-todo'}`}>
              {libelleType(proj.project_type)}
            </span>
          )}
          {proj.project_service && (
            <span className="badge badge-service">{libelleService(proj.project_service)}</span>
          )}
          {chef && (
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              · Chef de projet : <strong style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{userDisplayName(chef)}</strong>
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button className="btn btn-primary" onClick={() => setShowEditProject(true)}>Modifier le projet</button>
          {actionsSecondaires.length > 0 && (
            <div className="menu-actions">
              <button
                className="btn"
                aria-haspopup="menu"
                aria-expanded={menuActions}
                onClick={() => setMenuActions(!menuActions)}
              >
                Actions ▾
              </button>
              {menuActions && (
                <div className="menu-actions-liste" role="menu">
                  {actionsSecondaires.map((a, i) => (
                    <React.Fragment key={a.label}>
                      {a.separateur && i > 0 && <div className="menu-actions-sep" />}
                      <button
                        type="button"
                        role="menuitem"
                        className={`menu-actions-item ${a.danger ? 'danger' : ''}`}
                        onClick={() => { setMenuActions(false); a.action(); }}
                      >
                        {a.label}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {archivage && <div className="error-message" style={{ marginBottom: 16 }}>{archivage}</div>}

      {proj.charte_etat && proj.charte_etat !== 'validee' && activeTab !== 'charte' && (
        <div className="maintenance-avertissement" style={{ marginBottom: 16 }}>
          <strong>
            {proj.charte_etat === 'brouillon'
              ? 'La charte de ce projet n’est pas encore rédigée.'
              : 'La charte de ce projet est en cours de validation.'}
          </strong>{' '}
          Le projet reste en brouillon tant qu’elle n’est pas validée par les chefs de
          service puis la direction.{' '}
          <button className="btn btn-sm" style={{ marginLeft: 6 }} onClick={() => setActiveTab('charte')}>
            Ouvrir la charte
          </button>
        </div>
      )}

      {bilanImport && (
        <div className="success-message" style={{ marginBottom: 16 }}>{bilanImport}</div>
      )}

      {proj.archived && (
        <div className="error-message" style={{ marginBottom: 16 }}>
          <strong>Projet archivé{proj.archived_at ? ` le ${formatDate(proj.archived_at)}` : ''}
          {proj.archived_by ? ` par ${proj.archived_by}` : ''}.</strong> Il n'apparaît plus dans
          le tableau de bord, le calendrier ni le suivi des achats. Les données sont conservées.
        </div>
      )}

      {proj.status === 'Cancelled' && proj.cancel_reason && (
        <div className="error-message" style={{ marginBottom: 16 }}>
          <strong>Projet annulé.</strong> Raison : {proj.cancel_reason}
        </div>
      )}

      <div className="detail-stats">
        <div>
          <div className="detail-stat-label" title="Part des tâches du projet terminées">Tâches terminées</div>
          <div className="detail-stat-value" style={{ color: 'var(--accent)' }}>{proj.progress_percent}%</div>
        </div>
        <div>
          <div className="detail-stat-label">Budget alloué</div>
          <div className="detail-stat-value">
            {proj.budget_allocated > 0 ? formatMontant(proj.budget_allocated) : <span className="valeur-absente">Non renseigné</span>}
          </div>
          {proj.budget_allocated > 0 && (
            <div className="budget-stat-sub">
              {formatMontant(proj.budget_engaged || 0)} engagés · reste {formatMontant(proj.budget_remaining || 0)}
            </div>
          )}
        </div>
        <div>
          <div className="detail-stat-label">Tâches ouvertes</div>
          <div className="detail-stat-value">{openCount}</div>
        </div>
        <div>
          <div className="detail-stat-label">Échéance</div>
          <div className="detail-stat-value">
            {proj.end_date ? formatDate(proj.end_date) : <span className="valeur-absente">Non fixée</span>}
          </div>
        </div>
      </div>

      <div className="tab-bar" role="tablist" aria-label="Sections du projet">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            className={`tab-item ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <p>Chargement des tâches...</p>}
      {error && <div className="error-message">{error}</div>}

      {!loading && !error && (
        <>
          {activeTab === 'planif' && (
            <>
              <div className="planif-barre">
                <div className="view-toggle" role="tablist" aria-label="Vue de planification">
                  {VUES_PLANIF.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      role="tab"
                      aria-selected={vuePlanif === v.key}
                      className={`view-toggle-btn ${vuePlanif === v.key ? 'active' : ''}`}
                      onClick={() => setVuePlanif(v.key)}
                    >
                      {v.label}
                      {v.key === 'jalons' && tasks.some((t) => t.est_jalon) && (
                        <span style={{ marginLeft: 4, color: 'var(--text-muted)' }}>
                          ({tasks.filter((t) => t.est_jalon && t.status === 'Done').length}/{tasks.filter((t) => t.est_jalon).length})
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {vuePlanif !== 'jalons' && (
                  <button className="btn btn-primary btn-sm" onClick={() => handleOpenTask(null, 'Todo')}>
                    + Ajouter une tâche
                  </button>
                )}
              </div>

              {vuePlanif === 'kanban' && (
                <Kanban tasks={travail} users={users} onOpenTask={handleOpenTask} onTaskUpdated={loadTasks} />
              )}
              {vuePlanif === 'gantt' && (
                <Gantt
                  tasks={tasks}
                  users={users}
                  onOpenTask={handleOpenTask}
                  onReorderJalons={reordonnerJalons}
                  onJalonChanged={() => { loadTasks(true); rafraichirProjet(); }}
                />
              )}
              {vuePlanif === 'calendar' && (
                <CalendarView tasks={tasks} onOpenTask={(t) => (t.est_jalon ? setVuePlanif('jalons') : handleOpenTask(t))} />
              )}
              {vuePlanif === 'list' && (
                <TaskListView
                  tasks={travail}
                  users={users}
                  onOpenTask={handleOpenTask}
                  commentCounts={commentCounts}
                  attachmentCounts={attachmentCounts}
                />
              )}
              {vuePlanif === 'jalons' && (
                <MilestonesPanel
                  project={proj}
                  tasks={tasks}
                  users={users}
                  onChanged={() => { loadTasks(); rafraichirProjet(); }}
                />
              )}
            </>
          )}
          {activeTab === 'charte' && (
            <ProjectCharter
              project={proj}
              users={users}
              currentUser={currentUser}
              onChanged={rafraichirProjet}
            />
          )}
          {activeTab === 'budget' && (
            <ProjectBudget
              project={proj}
              tasks={travail}
              currentUser={currentUser}
              onBudgetChanged={rafraichirProjet}
            />
          )}
          {activeTab === 'deliverables' && (
            <Deliverables projectId={proj.id} users={users} currentUser={currentUser} tasks={travail} />
          )}
          {activeTab === 'activity' && (
            <>
              <ProgressChart points={historique} titre="Avancement du projet dans le temps" />
              <ActivityLog projectId={proj.id} users={users} />
            </>
          )}
          {activeTab === 'report' && (
            <ProjectReport project={proj} tasks={tasks} users={users} deliverableCount={deliverableCount} />
          )}
        </>
      )}

      {openTask !== undefined && (
        <TaskModal
          task={openTask}
          projectId={proj.id}
          users={users}
          currentUser={currentUser}
          initialStatus={newTaskStatus}
          onClose={() => setOpenTask(undefined)}
          onSaved={handleTaskSaved}
          onDeleted={handleTaskDeleted}
        />
      )}

      {showFusion && (
        <MergeTasksModal
          project={proj}
          onClose={() => setShowFusion(false)}
          onDone={() => { loadTasks(); loadCounts(); rafraichirProjet(); }}
        />
      )}

      {showImport && (
        <ImportTasksModal
          project={proj}
          users={users}
          onClose={() => setShowImport(false)}
          onImported={(bilan) => {
            setShowImport(false);
            setBilanImport(
              `${bilan.created} tâche(s) importée(s)` +
                (bilan.checklist_items ? ` et ${bilan.checklist_items} élément(s) de checklist.` : '.')
            );
            setActiveTab('planif');
            setVuePlanif('list');
            loadTasks();
            loadCounts();
            rafraichirProjet();
          }}
        />
      )}

      {showEditProject && (
        <ProjectModal
          project={proj}
          users={users}
          onClose={() => setShowEditProject(false)}
          onSaved={(updated) => { setProj(updated); setShowEditProject(false); if (onProjectChanged) onProjectChanged(updated); }}
          onDeleted={() => { setShowEditProject(false); onBack(); if (onProjectChanged) onProjectChanged(null); }}
        />
      )}

      {showCancelProject && (
        <CancelProjectModal
          project={proj}
          currentUser={currentUser}
          onClose={() => setShowCancelProject(false)}
          onCancelled={(updated) => { setProj(updated); setShowCancelProject(false); if (onProjectChanged) onProjectChanged(updated); }}
        />
      )}
    </div>
  );
}

export default ProjectDetail;
